import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import SafeIcon from '../common/SafeIcon';
import AudioButton from '../components/AudioButton';
import * as FiIcons from 'react-icons/fi';

const { 
  FiPlay, FiArrowRight, FiArrowLeft, FiCheck, FiX, 
  FiRefreshCw, FiStar, FiAward, FiUsers, FiSmile, 
  FiHeart, FiZap, FiTarget, FiVolume2, FiClock, 
  FiGift, FiUser, FiCpu, FiHeadphones, FiCheckCircle, 
  FiBook, FiGlobe 
} = FiIcons;

const Exercises = ({ updateProgress, setCurrentSection }) => {
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [showCelebration, setShowCelebration] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [exerciseCompleted, setExerciseCompleted] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [timeSpent, setTimeSpent] = useState(0);
  const [exerciseStartTime, setExerciseStartTime] = useState(Date.now());
  const [attemptedExercises, setAttemptedExercises] = useState(new Set());

  useEffect(() => {
    setCurrentSection('exercises');
    updateProgress('exercises');
    setExerciseStartTime(Date.now());
  }, []);

  // Enhanced exercise definitions covering ALL pronouns 
  const exercises = [
    {
      id: 'pronoun-basics',
      type: 'multiple-choice',
      category: 'Pronoun Basics',
      difficulty: 'Easy',
      points: 100,
      timeLimit: 60,
      title: 'Choose the Right Pronoun!',
      question: 'How do you say "I" in Amharic?',
      instructions: 'Select the correct Amharic word for "I"',
      hint: 'Think about referring to yourself',
      options: [
        { id: 'a', text: 'እኔ (ene)', correct: true, explanation: 'Correct! እኔ means "I"' },
        { id: 'b', text: 'እኛ (egna)', correct: false, explanation: 'እኛ means "we", not "I"' },
        { id: 'c', text: 'አንተ (ante)', correct: false, explanation: 'አንተ means "you" (masculine)' },
        { id: 'd', text: 'እሱ (essu)', correct: false, explanation: 'እሱ means "he"' }
      ],
      icon: FiUser,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    {
      id: 'pronoun-he',
      type: 'multiple-choice',
      category: 'Pronoun Basics',
      difficulty: 'Easy',
      points: 100,
      timeLimit: 60,
      title: 'Third Person Masculine',
      question: 'How do you say "He" in Amharic?',
      instructions: 'Select the correct word for referring to a male.',
      hint: 'It sounds like "Essu".',
      options: [
        { id: 'a', text: 'እሷ (esswa)', correct: false, explanation: 'እሷ means "She".' },
        { id: 'b', text: 'እሱ (essu)', correct: true, explanation: 'Correct! እሱ means "He".' },
        { id: 'c', text: 'አንተ (ante)', correct: false, explanation: 'አንተ is "You" (masculine).' },
        { id: 'd', text: 'እነሱ (ennesu)', correct: false, explanation: 'እነሱ means "They".' }
      ],
      icon: FiUser,
      color: 'from-indigo-500 to-blue-500',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200'
    },
    {
      id: 'pronoun-she',
      type: 'multiple-choice',
      category: 'Pronoun Basics',
      difficulty: 'Easy',
      points: 100,
      timeLimit: 60,
      title: 'Third Person Feminine',
      question: 'How do you say "She" in Amharic?',
      instructions: 'Select the correct word for referring to a female.',
      hint: 'It has a "wa" sound at the end.',
      options: [
        { id: 'a', text: 'እሱ (essu)', correct: false, explanation: 'እሱ means "He".' },
        { id: 'b', text: 'እሷ (esswa)', correct: true, explanation: 'Correct! እሷ means "She".' },
        { id: 'c', text: 'አንቺ (anchi)', correct: false, explanation: 'አንቺ is "You" (feminine).' },
        { id: 'd', text: 'እኔ (ene)', correct: false, explanation: 'እኔ means "I".' }
      ],
      icon: FiUser,
      color: 'from-pink-500 to-rose-500',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-200'
    },
    {
      id: 'respect-levels',
      type: 'scenario',
      category: 'Social Respect',
      difficulty: 'Medium',
      points: 150,
      timeLimit: 90,
      title: 'Choose the Respect Level!',
      question: 'You are meeting your grandmother for the first time today. How do you show respect?',
      instructions: 'Select the appropriate greeting that shows respect to elders',
      hint: 'Elders deserve formal respect in Ethiopian culture',
      scenario: {
        setting: 'Family Gathering',
        characters: ['You (child)', 'Grandmother'],
        context: 'First meeting, showing respect'
      },
      options: [
        { id: 'a', text: 'አንተ እንዴት ነህ? (Ante endet nesh?)', correct: false, explanation: 'Too casual! አንተ is for friends/peers' },
        { id: 'b', text: 'እርሶ እንዴት ነዎት? (Erswo endet newot?)', correct: true, explanation: 'Perfect! እርሶ shows respect to elders' },
        { id: 'c', text: 'እኔ ደህና ነኝ (Ene dehna negn)', correct: false, explanation: 'This means "I am well", not a greeting' },
        { id: 'd', text: 'ሰላም (Selam)', correct: false, explanation: 'While friendly, ሰላም lacks the formal respect needed' }
      ],
      icon: FiHeart,
      color: 'from-rose-500 to-pink-500',
      bgColor: 'bg-rose-50',
      borderColor: 'border-rose-200'
    },
    {
      id: 'pronoun-casual-fem',
      type: 'scenario',
      category: 'Casual Interaction',
      difficulty: 'Medium',
      points: 120,
      timeLimit: 60,
      title: 'Talking to a Sister',
      question: 'You are chatting with your sister. Which "You" do you use?',
      instructions: 'Choose the casual feminine pronoun.',
      hint: 'Use the feminine casual form "Anchi".',
      scenario: {
        setting: 'Home',
        characters: ['You', 'Sister'],
        context: 'Casual conversation'
      },
      options: [
        { id: 'a', text: 'አንተ (ante)', correct: false, explanation: 'Incorrect. አንተ is for males.' },
        { id: 'b', text: 'እርሶ (erswo)', correct: false, explanation: 'Too formal for a sibling!' },
        { id: 'c', text: 'አንቺ (anchi)', correct: true, explanation: 'Correct! አንቺ is used for female friends and family.' }
      ],
      icon: FiSmile,
      color: 'from-purple-500 to-fuchsia-500',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    },
    {
      id: 'family-unity',
      type: 'drag-drop',
      category: 'Cultural Values',
      difficulty: 'Medium',
      points: 120,
      timeLimit: 75,
      title: 'Complete the Family Phrase!',
      question: 'Complete this important family phrase: "_____ ቤተሰብ" (Our family)',
      instructions: 'Drag "Our" (የእኛ) to the box to complete the phrase.',
      hint: 'In Ethiopian culture, family is collective ("We/Our"), not individual ("My").',
      options: [
        { id: 'a', text: 'የእኔ (Ye-ene)', meaning: 'My', correct: false },
        { id: 'b', text: 'የእኛ (Ye-egna)', meaning: 'Our', correct: true },
        { id: 'c', text: 'የአንተ (Ye-ante)', meaning: 'Your', correct: false }
      ],
      explanation: 'Correct! "የእኛ" (Ye-egna) means "Our". We say "Our family" (የእኛ ቤተሰብ) to show unity.',
      icon: FiUsers,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    {
      id: 'pronoun-plural-you',
      type: 'scenario',
      category: 'Group Interaction',
      difficulty: 'Medium',
      points: 130,
      timeLimit: 60,
      title: 'Addressing the Group',
      question: 'You are speaking to a group of friends. How do you say "You all"?',
      instructions: 'Select the plural "You".',
      hint: 'The word starts with "Enn-".',
      scenario: {
        setting: 'Playground',
        characters: ['You', 'Friends'],
        context: 'Group conversation'
      },
      options: [
        { id: 'a', text: 'እኛ (egna)', correct: false, explanation: 'እኛ means "We".' },
        { id: 'b', text: 'እናንተ (ennante)', correct: true, explanation: 'Correct! እናንተ is "You all".' },
        { id: 'c', text: 'እነሱ (ennesu)', correct: false, explanation: 'እነሱ means "They".' }
      ],
      icon: FiUsers,
      color: 'from-teal-500 to-cyan-500',
      bgColor: 'bg-teal-50',
      borderColor: 'border-teal-200'
    },
    {
      id: 'pronoun-they',
      type: 'multiple-choice',
      category: 'Plural Pronouns',
      difficulty: 'Medium',
      points: 110,
      timeLimit: 60,
      title: 'Referring to Them',
      question: 'Which word means "They"?',
      instructions: 'Select the third person plural pronoun.',
      hint: 'Used to talk about a group of other people.',
      options: [
        { id: 'a', text: 'እነሱ (ennesu)', correct: true, explanation: 'Correct! እነሱ means "They".' },
        { id: 'b', text: 'እኛ (egna)', correct: false, explanation: 'እኛ is "We".' },
        { id: 'c', text: 'እሱ (essu)', correct: false, explanation: 'እሱ is "He".' },
        { id: 'd', text: 'እናንተ (ennante)', correct: false, explanation: 'እናንተ is "You all".' }
      ],
      icon: FiUsers,
      color: 'from-orange-500 to-amber-500',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    },
    {
      id: 'church-context',
      type: 'contextual',
      category: 'Religious Context',
      difficulty: 'Hard',
      points: 200,
      timeLimit: 120,
      title: 'Church Respect Protocol!',
      question: 'You are in church and need to speak to the priest. How do you address him?',
      instructions: 'Choose the most respectful form of address in a religious setting',
      hint: 'Religious figures deserve the highest level of respect',
      scenario: {
        setting: 'Ethi Orthodox Church',
        characters: ['You', 'Priest'],
        context: 'Formal religious ceremony'
      },
      options: [
        { id: 'a', text: 'አባቴ (Abate)', correct: false, explanation: 'Too informal - "my father" is for actual father' },
        { id: 'b', text: 'አባታችን (Abatachin)', correct: true, explanation: 'Excellent! "Our father" shows collective reverence' },
        { id: 'c', text: 'እሱ (Essu)', correct: false, explanation: 'Too casual - just "he"' },
        { id: 'd', text: 'ጌታዬ (Getay)', correct: false, explanation: 'Respectful but not the traditional church form' }
      ],
      icon: FiAward,
      color: 'from-purple-500 to-indigo-500',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200'
    },
    {
      id: 'sound-matching',
      type: 'audio-matching',
      category: 'Pronunciation',
      difficulty: 'Easy',
      points: 180,
      timeLimit: 90,
      title: 'Sound Recognition Challenge!',
      question: 'Listen to the sound. Which word matches?',
      instructions: 'Click the play button to hear the word, then select the correct meaning below.',
      hint: 'The sound is a common greeting.',
      targetAudio: 'selam',
      options: [
        { id: 'a', text: 'ሰላም (Selam)', meaning: 'Hello/Peace', correct: true },
        { id: 'b', text: 'አመሰግናለሁ (Amesegualehu)', meaning: 'Thank you', correct: false },
        { id: 'c', text: 'ደህና (Dehna)', meaning: 'I am well', correct: false }
      ],
      explanation: 'The sound was "Selam", which means Hello or Peace.',
      icon: FiHeadphones,
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200'
    },
    {
      id: 'scenario-teacher',
      type: 'scenario',
      category: 'Real World',
      difficulty: 'Hard',
      points: 250,
      timeLimit: 60,
      title: 'Real-Life Scenario: Teacher',
      question: 'You encounter your teacher at school. How do you greet them?',
      instructions: 'Choose the correct respectful response.',
      hint: 'Teachers are authority figures and require formal address.',
      scenario: {
        setting: 'School',
        characters: ['You', 'Teacher'],
        context: 'Formal Greeting'
      },
      options: [
        { id: 'a', text: 'አንተ እንዴት ነህ?', correct: false, explanation: 'Incorrect. This is too casual for a teacher.' },
        { id: 'b', text: 'እርሶ እንዴት ነዎት?', correct: true, explanation: 'Correct! "እርሶ እንዴት ነዎት?" is the proper respectful greeting.' },
        { id: 'c', text: 'እናንተ እንዴት ናችሁ?', correct: false, explanation: 'Incorrect. While respectful, the verb agreement is not ideal here.' }
      ],
      icon: FiAward,
      color: 'from-indigo-500 to-purple-500',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200'
    },
    {
      id: 'scenario-friend',
      type: 'scenario',
      category: 'Real World',
      difficulty: 'Easy',
      points: 100,
      timeLimit: 45,
      title: 'Real-Life Scenario: Friend',
      question: 'You meet a male friend. How do you greet him?',
      instructions: 'Choose the appropriate casual response.',
      hint: 'Friends use casual language with each other.',
      scenario: {
        setting: 'Neighborhood',
        characters: ['You', 'Friend'],
        context: 'Casual Greeting'
      },
      options: [
        { id: 'a', text: 'አንተ እንዴት ነህ?', correct: true, explanation: 'Correct! "አንተ እንዴት ነህ?" is the standard casual greeting for a male friend.' },
        { id: 'b', text: 'እርሶ እንዴት ነዎት?', correct: false, explanation: 'Too formal. You don\'t use "እርሶ" with close friends.' },
        { id: 'c', text: 'እርሶ እንዴት ናችሁ?', correct: false, explanation: 'Too formal.' }
      ],
      icon: FiSmile,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    {
      id: 'possessive-my',
      type: 'multiple-choice',
      category: 'Possessive Suffixes',
      difficulty: 'Medium',
      points: 140,
      timeLimit: 60,
      title: 'Possessive: My House',
      question: 'How do you say "My House" (Bet + suffix)?',
      instructions: 'Choose the word with the correct suffix for "My".',
      hint: 'The suffix for "my" is "-e".',
      options: [
        { id: 'a', text: 'ቤቴ (Bete)', correct: true, explanation: 'Correct! "-e" suffix makes it "My house".' },
        { id: 'b', text: 'ቤትህ (Betih)', correct: false, explanation: 'This means "Your house" (masc).' },
        { id: 'c', text: 'ቤቷ (Betwa)', correct: false, explanation: 'This means "Her house".' }
      ],
      icon: FiHeadphones,
      color: 'from-amber-500 to-yellow-500',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200'
    }
  ];

  const currentExercise = exercises[currentExerciseIndex];

  // Timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeSpent(Math.floor((Date.now() - exerciseStartTime) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [exerciseStartTime]);

  const handleAnswer = (answerId) => {
    if (showFeedback) return;
    
    setSelectedOption(answerId);
    setIsAnimating(true);
    
    setTimeout(() => {
      const isCorrect = currentExercise.options?.find(opt => opt.id === answerId)?.correct;
      const newAnswers = { ...userAnswers, [currentExerciseIndex]: { selected: answerId, correct: isCorrect } };
      setUserAnswers(newAnswers);
      setAttemptedExercises(prev => new Set([...prev, currentExerciseIndex]));
      
      setShowFeedback(true);
      if (isCorrect) {
        setScore(score + currentExercise.points);
        setStreak(streak + 1);
        if (streak + 1 >= 3) {
          setShowCelebration(true);
          setTimeout(() => setShowCelebration(false), 2000);
        }
      } else {
        setStreak(0);
      }
      setIsAnimating(false);
    }, 500);
  };

  const nextExercise = () => {
    if (currentExerciseIndex < exercises.length - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
      setShowFeedback(false);
      setSelectedOption(null);
      setShowHint(false);
      setExerciseStartTime(Date.now());
    } else {
      setExerciseCompleted(true);
    }
  };

  const previousExercise = () => {
    if (currentExerciseIndex > 0) {
      setCurrentExerciseIndex(currentExerciseIndex - 1);
      setShowFeedback(false);
      setSelectedOption(null);
      setShowHint(false);
      setExerciseStartTime(Date.now());
    }
  };

  const resetExercises = () => {
    setCurrentExerciseIndex(0);
    setUserAnswers({});
    setShowFeedback(false);
    setScore(0);
    setStreak(0);
    setSelectedOption(null);
    setExerciseCompleted(false);
    setShowHint(false);
    setAttemptedExercises(new Set());
    setExerciseStartTime(Date.now());
  };

  const renderExerciseContent = () => {
    switch (currentExercise.type) {
      case 'multiple-choice':
        return renderMultipleChoice();
      case 'scenario':
        return renderScenarioExercise();
      case 'drag-drop':
        return renderDragDrop();
      case 'contextual':
        return renderContextualExercise();
      case 'audio-matching':
        return renderAudioMatching();
      default:
        return renderMultipleChoice();
    }
  };

  const renderMultipleChoice = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {currentExercise.options.map((option, index) => (
          <button
            key={option.id}
            className={`group relative p-6 rounded-xl border-2 transition-all duration-300 
              ${isAnimating ? 'animate-pulse' : ''}
              ${showFeedback && option.correct ? 'border-green-500 bg-green-50' : ''}
              ${showFeedback && selectedOption === option.id && !option.correct ? 'border-red-500 bg-red-50' : ''}
              ${selectedOption === option.id && !showFeedback ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'}
            `}
            onClick={() => !showFeedback && handleAnswer(option.id)}
            disabled={showFeedback}
          >
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 group-hover:bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-600">
                {String.fromCharCode(65 + index)}
              </div>
              <div className="flex-1 text-left">
                <span className="font-amharic text-lg font-medium text-gray-800">{option.text}</span>
              </div>
              {showFeedback && (
                <div className="flex-shrink-0">
                  {option.correct ? (
                    <SafeIcon icon={FiCheck} className="text-green-600 text-xl" />
                  ) : selectedOption === option.id ? (
                    <SafeIcon icon={FiX} className="text-red-600 text-xl" />
                  ) : null}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderScenarioExercise = () => (
    <div className="space-y-6">
      <div className={`${currentExercise.bgColor} rounded-xl p-6 border ${currentExercise.borderColor}`}>
        <div className="flex items-center gap-4 mb-4">
          <div className="flex-shrink-0">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white shadow-lg">
              <SafeIcon icon={currentExercise.icon} className="text-2xl" />
            </div>
          </div>
          <div>
            <h4 className="font-bold text-gray-800">{currentExercise.scenario?.setting}</h4>
            <p className="text-sm text-gray-600">{currentExercise.scenario?.context}</p>
          </div>
        </div>
        
        <div className="flex justify-center gap-8 mb-6">
          {currentExercise.scenario?.characters.map((character, idx) => (
            <div key={idx} className="text-center">
              <div className="w-20 h-20 rounded-xl bg-white border-2 border-blue-300 flex items-center justify-center text-3xl mb-2 shadow-sm">
                {character === 'You (child)' || character === 'You' ? '👦' : 
                 character === 'Grandmother' ? '👵' : 
                 character === 'Teacher' ? '👨‍🏫' : 
                 character === 'Priest' ? '⛪' : 
                 character === 'Sister' ? '👧' : 
                 character === 'Friends' ? '🧑‍🤝‍🧑' : '🧑'}
              </div>
              <span className="text-xs text-gray-600 font-medium">{character}</span>
            </div>
          ))}
        </div>
      </div>
      {renderMultipleChoice()}
    </div>
  );

  const renderDragDrop = () => {
    const handleDragStart = (e, id) => {
      e.dataTransfer.setData("text/plain", id);
      e.dataTransfer.effectAllowed = "copy";
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    };

    const handleDrop = (e) => {
      e.preventDefault();
      const id = e.dataTransfer.getData("text/plain");
      if (id && !showFeedback) {
        handleAnswer(id);
      }
    };

    const selectedItem = selectedOption ? currentExercise.options.find(opt => opt.id === selectedOption) : null;

    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="inline-block bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-8 border border-green-200">
            <div className="text-3xl font-bold text-gray-800 mb-6 flex items-center justify-center gap-3 flex-wrap">
              <div 
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className={`min-w-[140px] h-20 border-3 border-dashed rounded-xl flex items-center justify-center px-4 transition-all
                  ${showFeedback ? 
                    (selectedItem?.correct ? 'bg-green-100 border-green-500 text-green-800' : 'bg-red-100 border-red-500 text-red-800') : 
                    'bg-white border-gray-400 text-gray-400'}
                `}
              >
                {selectedItem ? (
                  <span className="font-amharic text-xl font-bold">{selectedItem.text.split(' ')[0]}</span>
                ) : (
                  <span className="text-sm">Drag Here</span>
                )}
              </div>
              <span className="font-amharic">ቤተሰብ</span>
            </div>
            
            <p className="text-sm text-gray-600 mb-6">Drag the correct word to the box or click to select</p>
            
            <div className="flex justify-center gap-4 flex-wrap">
              {currentExercise.options?.map((item) => (
                <div
                  key={item.id}
                  draggable={!showFeedback}
                  onDragStart={(e) => handleDragStart(e, item.id)}
                  onClick={() => !showFeedback && handleAnswer(item.id)}
                  className={`bg-white border-2 rounded-xl p-4 transition-all shadow-sm
                    ${showFeedback ? 'opacity-50 cursor-not-allowed' : 'cursor-move hover:border-blue-400 hover:shadow-md cursor-pointer active:scale-95'}
                    ${selectedOption === item.id ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-300'}
                  `}
                >
                  <span className="font-amharic text-lg font-medium text-gray-800">{item.text}</span>
                  <span className="block text-sm text-gray-500 mt-1">{item.meaning}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderContextualExercise = () => (
    <div className="space-y-6">
      <div className={`${currentExercise.bgColor} rounded-xl p-6 border ${currentExercise.borderColor}`}>
        <div className="flex items-center gap-6">
          <div className="text-5xl">⛪</div>
          <div>
            <h4 className="font-bold text-gray-800 mb-2">{currentExercise.scenario?.setting}</h4>
            <p className="text-gray-600">{currentExercise.scenario?.context}</p>
          </div>
        </div>
      </div>
      {renderMultipleChoice()}
    </div>
  );

  const renderAudioMatching = () => (
    <div className="space-y-6 text-center">
      {/* Player Area */}
      <div className={`${currentExercise.bgColor} rounded-2xl p-8 border ${currentExercise.borderColor} inline-block shadow-sm`}>
        <div className="mb-4">
          <SafeIcon icon={FiVolume2} className="text-4xl text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 font-medium">Click to Listen</p>
        </div>
        <div className="transform scale-150">
          <AudioButton text={currentExercise.targetAudio} />
        </div>
      </div>

      {/* Options */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
        {currentExercise.options?.map((option) => (
          <button
            key={option.id}
            onClick={() => !showFeedback && handleAnswer(option.id)}
            disabled={showFeedback}
            className={`group relative p-6 rounded-xl border-2 transition-all duration-300 
              ${showFeedback && option.correct ? 'border-green-500 bg-green-50' : ''}
              ${showFeedback && selectedOption === option.id && !option.correct ? 'border-red-500 bg-red-50' : ''}
              ${selectedOption === option.id && !showFeedback ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-md'}
            `}
          >
            <span className="block text-xl font-bold text-gray-800 mb-2 font-amharic">{option.text.split(' ')[0]}</span>
            <span className="block text-sm text-gray-500">{option.meaning}</span>
            {showFeedback && option.correct && (
              <div className="absolute top-2 right-2">
                <SafeIcon icon={FiCheck} className="text-green-600" />
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return 'text-green-600 bg-green-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Hard': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (exerciseCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
            <div className="mb-8">
              <SafeIcon icon={FiAward} className="text-6xl text-yellow-500 mx-auto mb-4" />
              <h1 className="text-3xl font-bold text-gray-800 mb-2">🎉 Amazing Achievement!</h1>
              <p className="text-gray-600 text-lg">You've completed all exercises!</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gray-50 rounded-xl p-6">
                <SafeIcon icon={FiStar} className="text-3xl text-yellow-500 mb-2" />
                <div className="text-2xl font-bold text-gray-800">{score}</div>
                <div className="text-sm text-gray-500">Total Points</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <SafeIcon icon={FiZap} className="text-3xl text-orange-500 mb-2" />
                <div className="text-2xl font-bold text-gray-800">{streak}</div>
                <div className="text-sm text-gray-500">Best Streak</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <SafeIcon icon={FiClock} className="text-3xl text-blue-500 mb-2" />
                <div className="text-2xl font-bold text-gray-800">{formatTime(timeSpent)}</div>
                <div className="text-sm text-gray-500">Time Spent</div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="btn-primary" onClick={resetExercises}>
                <SafeIcon icon={FiRefreshCw} /> Try Again
              </button>
              <Link to="/audio" className="btn-secondary">
                Continue to Audio Gallery <SafeIcon icon={FiArrowRight} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-4 md:p-6">
      {/* Celebration Overlay */}
      {showCelebration && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 text-center max-w-sm animate-bounce">
            <SafeIcon icon={FiStar} className="text-6xl text-yellow-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">🔥 Amazing Streak!</h2>
            <p className="text-gray-600 mb-4">You got {streak + 1} correct in a row!</p>
            <div className="flex items-center justify-center gap-2 text-purple-600 font-bold">
              <SafeIcon icon={FiGift} className="text-3xl" />
              <span>+50 Bonus Points!</span>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto">
        {/* Exercise Progress Overview */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border border-gray-100">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${currentExercise.color} flex items-center justify-center text-white shadow-lg`}>
                <SafeIcon icon={currentExercise.icon} className="text-2xl" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">{currentExercise.title}</h1>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full ${getDifficultyColor(currentExercise.difficulty)}`}>
                    {currentExercise.difficulty}
                  </span>
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-purple-100 text-purple-600">
                    <SafeIcon icon={currentExercise.icon} /> {currentExercise.category}
                  </span>
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-600">
                    <SafeIcon icon={FiClock} /> {formatTime(timeSpent)} / {formatTime(currentExercise.timeLimit)}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="bg-gray-50 rounded-xl p-3 text-center min-w-[70px]">
                <SafeIcon icon={FiAward} className="text-yellow-500 text-lg mb-1" />
                <div className="text-xl font-bold text-gray-800">{score}</div>
                <div className="text-xs text-gray-500">Points</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 text-center min-w-[70px]">
                <SafeIcon icon={FiZap} className="text-orange-500 text-lg mb-1" />
                <div className="text-xl font-bold text-gray-800">{streak}</div>
                <div className="text-xs text-gray-500">Streak</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 text-center min-w-[70px]">
                <SafeIcon icon={FiTarget} className="text-green-500 text-lg mb-1" />
                <div className="text-xl font-bold text-gray-800">{currentExerciseIndex + 1}/{exercises.length}</div>
                <div className="text-xs text-gray-500">Progress</div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2 text-sm text-gray-600">
              <span>Exercise {currentExerciseIndex + 1} of {exercises.length}</span>
              <span>{Math.round(((currentExerciseIndex + 1) / exercises.length) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500 ease-out" 
                style={{ width: `${((currentExerciseIndex + 1) / exercises.length) * 100}%` }} 
              />
            </div>
          </div>

          {/* Exercise Navigation */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {exercises.map((exercise, idx) => (
                <button
                  key={idx}
                  onClick={() => idx < currentExerciseIndex && previousExercise()}
                  disabled={idx >= currentExerciseIndex}
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all
                    ${idx === currentExerciseIndex ? 'bg-blue-500 text-white' : 
                      attemptedExercises.has(idx) ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}
                    ${idx < currentExerciseIndex ? 'cursor-pointer hover:bg-gray-200' : 'cursor-not-allowed'}
                  `}
                >
                  {attemptedExercises.has(idx) && idx !== currentExerciseIndex ? (
                    <SafeIcon icon={FiCheck} />
                  ) : (
                    idx + 1
                  )}
                </button>
              ))}
            </div>
            <div className="text-sm text-gray-500">
              {attemptedExercises.size} of {exercises.length} attempted
            </div>
          </div>
        </div>

        {/* Main Exercise Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
          <div className="mb-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">{currentExercise.question}</h2>
                <p className="text-gray-600 text-lg">{currentExercise.instructions}</p>
              </div>
              <button 
                className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg font-medium hover:bg-purple-200 transition-colors"
                onClick={() => setShowHint(!showHint)}
              >
                <SafeIcon icon={FiGift} /> {showHint ? 'Hide Hint' : 'Show Hint'}
              </button>
            </div>
            
            {showHint && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-center gap-3">
                <SafeIcon icon={FiGift} className="text-purple-500 text-xl" />
                <span className="text-purple-700">{currentExercise.hint}</span>
              </div>
            )}
          </div>

          <div className="mb-6">
            {renderExerciseContent()}
          </div>

          {showFeedback && (
            <div className={`rounded-xl p-6 text-center ${userAnswers[currentExerciseIndex]?.correct ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
              <div className="flex items-center justify-center gap-4 mb-4">
                <div>
                  {userAnswers[currentExerciseIndex]?.correct ? (
                    <SafeIcon icon={FiAward} className="text-3xl text-yellow-500" />
                  ) : (
                    <SafeIcon icon={FiHeart} className="text-3xl text-red-500" />
                  )}
                </div>
                <div className="text-left max-w-md">
                  <h4 className={`text-lg font-bold mb-2 ${userAnswers[currentExerciseIndex]?.correct ? 'text-green-700' : 'text-red-700'}`}>
                    {userAnswers[currentExerciseIndex]?.correct ? 'Excellent!' : 'Keep Learning!'}
                  </h4>
                  <p className="text-gray-600 mb-3">
                    {currentExercise.options?.find(opt => opt.id === selectedOption)?.explanation || currentExercise.explanation}
                  </p>
                  {userAnswers[currentExerciseIndex]?.correct && (
                    <div className="flex items-center gap-2 text-yellow-600 font-bold">
                      <SafeIcon icon={FiStar} />
                      <span>+{currentExercise.points} points earned!</span>
                    </div>
                  )}
                </div>
              </div>
              
              <button 
                className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg transition-all"
                onClick={nextExercise}
              >
                {currentExerciseIndex < exercises.length - 1 ? 'Next Exercise' : 'Complete All'} <SafeIcon icon={FiArrowRight} />
              </button>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-6">
          <div className="flex gap-4">
            {currentExerciseIndex > 0 && (
              <button className="btn-secondary" onClick={previousExercise} >
                <SafeIcon icon={FiArrowLeft} /> Previous Exercise
              </button>
            )}
            <Link to="/cultural" className="btn-secondary">
              <SafeIcon icon={FiArrowLeft} /> Back to Cultural
            </Link>
          </div>
          <Link to="/audio" className="btn-primary">
            Next: Audio Gallery <SafeIcon icon={FiArrowRight} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Exercises;